import { Document, Types, mongo } from 'mongoose'
import {
  Dictionary,
  Installer,
  StreetAddress,
  LoggedUpload,
  LoanDocument,
  SolarDoc,
  CitizenshipValue,
  ITenant,
  Org,
  Individual,
  SIndividual,
  ApplicantRace,
  IncomeDocument,
  Incentive,
  FundReleaseRequest,
  Consent,
  YesNoEmpty,
  AmortizationSchedule,
  AccessLevel,
  CriteriaEvaluationResult,
  EquifaxDitReportDetail,
  Adder,
  EquipmentType,
  PrequalResult,
  Agents,
  EnvelopeRecord,
  EnvelopeKind,
  ClosingCostsDetail,
  DocSubType,
  MCGBAmortizationResult,
  formatted,
  monthDayYear,
  MCGBLoanCalculations
} from './index'
import type { ProjectCostCaps } from './project-cost-caps'
import type {
  SolarTradelineInquiryItem,
  SolarTradelineInquiryMergeOptions,
} from './solar-tradeline'
import { CCEF_APPLICATION_REFERRAL_SOURCE, CCEF_PLATFORM_LOAN_AMOUNT_THRESHOLD } from './constants'
import { StatusData } from './status-data'
import { PreSubmitStatus } from './presubmit-status'
import { Applicant } from './applicant'
import type { RequiredAdditionalInformation } from './required-additional-information'

export type StatusTokenPayload = {
  id: string
  uploadType?: string
  role?: 'applicant' | 'installer'
}

export type AirtableUserMeta = {
  id?: string
  email?: string
  name?: string
}

export type StepDocStatus = {
  requiredDocsCount: number
  approvedDocsCount: number
  docsNeedSupportActionCount: number
  docsNeedInstallerActionCount: number
  allDocsUploadedForStep: boolean
  allItemsComplete: boolean
}

export type ProjectType = 'residential' | 'commercial' | 'non-profit'
export type InstallerDocMap = Dictionary<Dictionary<LoanDocument>>
export type DitDecision = 'Approve' | 'Deny' | 'Review' | 'Error'
export type PersonaParty = 'borrower' | 'co-borrower'
export type IdentityVerificationVersion = 'legacy' | 'persona'

export type PersonaVerificationRecord = {
  inquiryId?: string
  status?: string
  verifiedAt?: Date
  lastEventAt?: Date
}

export type PersonaVerifications = {
  borrower?: PersonaVerificationRecord
  coBorrower?: PersonaVerificationRecord
}

export type IncompleteValidationOptions = {
  /**
   * Permits saving a joint application while the co-borrower's identity verification is
   * still pending, because the remaining co-borrower fields are not collected until that
   * verification completes. Must never be set on the submission path, which is the gate
   * that enforces a complete co-borrower.
   */
  allowPendingCoBorrowerVerification?: boolean
}

export enum FundingSchedule {
  FiftyFifty = "50_50",
  TwentyEighty = "20_80",
  SeventyThirty = "70_30",
  OneHundred = "100",
}

export const FundingScheduleLabels: Record<FundingSchedule, string> = {
  [FundingSchedule.FiftyFifty]: "50% at M1 / 50% at M2",
  [FundingSchedule.TwentyEighty]: "20% at M1 / 80% at M2",
  [FundingSchedule.SeventyThirty]: "70% at M1 / 30% at M2",
  [FundingSchedule.OneHundred]: "100% at final funding"
}

export enum FundReleaseType {
  RoofFundingRelease = "Roof Funding Release",
  InitialFundingRelease = "Initial Funding Release",
  FinalFundingRelease = "Final Funding Release",
  FullFundingRelease = "Full Funding Release"
}

export enum InstallerPatchableFields {
  adders,
  teslaPowerwallQty,
  storageQty,
  systemSizeKW,
  storageCapacity,
  ppWForPV,
  ppwForStorage,
  solarCost,
}

// not known if this is comprehensive list
export type Decision = "Approved-Pending Signed Contract" |
  "Approved" |
  "Booked (Solar + Roof)" |
  "Booked (Solar Only)" |
  "Canceled" |
  "Change Order Pending" |
  "Declined" |
  "Fully Funded" |
  "Loan Docs Requested" |
  "Partially Funded" |
  "Pending Phase 1 Funding" |
  "Pending Phase 2 Funding" |
  "Pending Roof Funding" |
  "Pending" |
  "Roof Funded" |
  "Withdrawn" |
  null


export type Trustee = {
  name: string,
  email: string,
  phone: string,
  address: StreetAddress,
}

export interface BaseLoanApplication extends Applicant {
  _id: string
  address: string
  airtableId: string
  appId?: number
  approvalDate?: Date
  assignee?: AirtableUserMeta
  applicationReceived: Date
  authorizedInstallers?: string[]
  avgMonthlyUtilityBill?: number
  estAvgMonthlyUtilityBillAfterSolar?: number
  customerName?: string
  businessName?: string
  projectType?: ProjectType
  principals?: Partial<Individual>[]
  incentive?: Incentive
  employerName: string
  occupation: string
  statedGrossAnnualIncome?: string // "$100,000.00"
  sourceLoanApplicationId?: typeof mongo.ObjectId
  loanNumber: string
  loanAccountNumber: string
  incentiveSelected: boolean
  applicationReferralSource?: string

  id: string
  installersStatus: string
  email: string
  loanDurationMonths: number
  loanAmount: number
  loanModFee?: number
  solarCost?: number
  systemCost?: number
  otherCost?: number
  systemSizeKW?: number
  ppWForPV?: number
  ppWForStorage?: number
  interest: number
  interestOnlyMonths?: number
  installerEmail?: string[]
  installerID: string
  source?: {
    kind: 'web' | 'api'
    integrationId: string
    key: string
  }
  branchId: string
  campaign: string
  decision?: string
  uploads?: LoggedUpload[]
  userAddress?: StreetAddress
  citizenship: CitizenshipValue
  race?: ApplicantRace
  statedHouseholdIncome?: string // "$100,000.00"
  coBorrowerAddress?: StreetAddress
  trustees?: Trustee[]

  // there's a bug where includeCoBorrower gets incorrectly set to 'no' despite being joint app;
  // accordingly includeCoBorrower considered unreliable except during initial eligibility 
  // check. Strongly prefer to use isJointApp instead.
  includeCoBorrower?: YesNoEmpty
  isJointApp: boolean
  isCoborrowerSpouse?: YesNoEmpty
  installationAddress?: StreetAddress
  aaNoticeGiven?: string
  nTPGiven?: string
  propertyAppraiserReviewed?: string,
  docPrepQueueDate?: string,
  contractReceived?: string,
  installedAtDifferentLocation?: YesNoEmpty
  incomeDocType?: 'paystubs' | 'W-2' | 'taxreturn'
  incomeVerificationDocuments?: SolarDoc[]
  useAutoDebit?: YesNoEmpty
  autoDebitAccountNo?: string
  autoDebitRoutingNo?: string
  autoDebitInstitution?: string
  agreesToTermsAndConditions?: Consent
  agreesToProceedWithHardCreditInquiry?: Consent
  agreesToWarningAgainstUsingOtherFinancing?: Consent
  agreesTo6MonthNoPaymentPeriod?: Consent
  agreesToProceedWithPrequal?: Consent
  agreesToContractDetails?: Consent
  agreesToIncomeAndFinancialInformation?: Consent
  agreesToLenderInspection?: Consent
  attestConsumerResourceUnderstanding?: Consent
  stateInstalled?: string
  propertyHeldInTrust?: YesNoEmpty
  previousEmployment?: string
  yearsAtEmployer?: number
  fundingSchedule: FundingSchedule
  monitoringSiteId?: string
  lenderInspectionRequestedTimestamp?: Date
  lenderInspectionRequiresBorrowerInvolvement?: boolean
  lenderInspectionVerifiedDate?: string
  fundReleasesRequested?: FundReleaseRequest[]
  finalFundReleaseAmount?: number
  equipmentType?: EquipmentType
  teslaPowerwallQty?: number
  storageQty?: number
  storageCapacity?: number
  // installWillCompleteIn30Days: YesNoEmpty

  coFirstName: string
  coMiddleName: string
  coLastName: string
  coSsn: string
  coPhone: string
  coEmail: string
  coBirthDate: string
  coDriversLicenseNo: string
  coDriversLicenseIssueDate: string
  coDriversLicenseIssuingState: string
  coDriversLicenseExpirationDate: string
  coCitizenship: CitizenshipValue
  coRace?: ApplicantRace
  coEmployerName: string
  coOccupation: string
  coYearsAtEmployer: number
  coPreviousEmployment: string
  coIncomeDocType?: 'paystubs' | 'W-2' | 'taxreturn'
  coIncomeVerificationDocuments?: SolarDoc[]
  coStatedGrossAnnualIncome: string // "$100,000.00" used for prequal
  coStatedHouseholdIncome?: string // "$100,000.00" reporting purposes only, not used for prequal
  coHasMortgage?: YesNoEmpty
  lookup?: string

  abrigoProposedLoanId?: number
  abrigoCoborrowerLoanRoleId?: number
  abrigoLoanApplicationId?: number
  abrigoWorkflowId?: number
  error: string

  isLocked?: boolean
  lockedTimestamp?: Date
  maxAllowableMonthlyPayment?: number
  prequalResult?: PrequalResult
  prequalTimestamp?: Date
  prequalResultCombined?: PrequalResult
  prequalResultCombinedTimestamp?: Date
  prequalError?: any
  prescreened?: boolean
  identityVerificationVersion?: IdentityVerificationVersion
  personaVerifications?: PersonaVerifications
  declinedToAddCoborrowerDate?: Date
  ditDecision?: DitDecision
  coDitDecision?: DitDecision
  hasOfacAlerts?: boolean
  hasSolarTradeline?: boolean
  solarTradelineInquiryItems?: SolarTradelineInquiryItem[]
  ofacAlerts?: EquifaxDitReportDetail[]
  coOfacAlerts?: EquifaxDitReportDetail[]
  ditError?: string
  requiredAdditionalInformation?: RequiredAdditionalInformation
  preSubmitStatus?: PreSubmitStatus
  slackLink?: string
  submissionResponse?: any,
  submittedFromIp?: string
  submittedFromTzOffset?: number
  submittedTimestamp?: Date
  oeAdminResubmitBy?: Types.ObjectId
  oeAdminResubmitTimestamp?: Date
  oeAdminResubmitOriginalError?: string
  tenant?: ITenant
  notesVisibleToInstallers?: string
  // consumerRequestedContractChanges?: {
  //   message: string
  //   timestamp: Date
  //   resolved?: boolean
  // }[]

  // should be duplicates of branchId, installerID, and campaign respectively
  utm_medium: string
  utm_source: string
  utm_campaign: string

  rim: string
  installerDocs?: InstallerDocMap
  envelopes?: EnvelopeRecord[]
  pendingBookingDate?: string // from airtable; important; tells us loan docs are signed
  loanDocsSignedDate?: Date // future when we have from docusign
  loanDocsIssuedDate?: string // from airtable; will be set initially when sending docs via docusign
  lastStatusChange?: Date
  lastModified?: Date
  lastModifiedBy?: AirtableUserMeta
  createdDate?: Date
  updatedDate?: Date
  preapprovalLetter?: {
    doc: SolarDoc
    contentMD5: string
    agreement?: Consent
  }
  installerProbationaryStatus?: string[]
  fundingBlock?: string
  projectCostCaps?: ProjectCostCaps

  // V1 new fields
  productId: string
  projectUrl?: string
  loanProposalId: string
  loanRequestId: string
  monthlyAverageEnergy: number
  adders: Adder[]

  // Loan Application Phase Airtable Fields
  contractNotes: string,
  loanDocumentsSentNotes: string,
  loanDocumentsSignedNotes: string,
  initialBankReviewNotes: string,
  homeOwnershipVerificationNotes: string,
  bankFinalReviewNotes: string,

  // Loan Funding Phase Airtable Fields
  roofPermittingComments: string,
  passedRoofInspectionComments: string,
  roofLienWaiverFormComments: string,
  roofFundReleaseRequestComments: string,
  roofFundingDisbursedComments: string,
  roofFundingDate: string,
  constructionPermitComments: string,
  engineeringPlansComments: string,
  initialFundReleaseRequestComments: string,
  initialFundingDisbursedComments: string,
  partialFundingDate: string,
  proofOfPassedInspectionComments: string,
  proofOfPermissionToOperateComments: string,
  contractorLienWaiverFormComments: string,
  finalFundingReleaseRequestComments: string,
  finalFundingDisbursedComments: string,
  fullFundingDate: string,
  fullFundingReleaseRequestComments: string,

  addOrUpdatePrincipal(p: Individual): SIndividual
  getReferralToken: () => string
  getStatusData: (access?: AccessLevel) => StatusData
  getStreet: () => string
  getDoc: (id: string) => LoanDocument | undefined
  docForSlot: (slot: string) => LoanDocument | undefined
  getInterestOnlyMonths: () => number
  getAmortization: (asOf?: Date) => AmortizationSchedule
  getRoofCost: () => number
  getFundReleaseAmounts: () => Record<string, number>
  getStepDocStatus: () => StepDocStatus
  isResidential: () => boolean
  isDeclined: () => boolean
  isPrequalified: () => YesNoEmpty
  processDecline: () => Promise<void>
  applySolarTradelineInquiry: (
    incoming: unknown,
    options: SolarTradelineInquiryMergeOptions
  ) => Promise<{ applied: boolean; declined: boolean }>
  lastUploadForStatus(status: string): LoggedUpload | undefined
  lastUploadForType(uploadType: string): LoggedUpload | undefined
  logUpload(
    uploadType: string,
    documents: LoanDocument[],
    uploadedBy?: string,
    notes?: string
  ): Promise<void>
  projectAddress: () => string
  estimatedPayment: () => number
  estimatedApr: () => number
  estimatedClosingCosts: () => number
  estimatedClosingCostsDetail: () => ClosingCostsDetail
  estimatedMcgbApr: () => number
  getMcgbIncentive: (asOf?: Date) => MCGBAmortizationResult
  getTotalMcgbIncentiveAmount: () => number
  handleAgentOutput: (run: Agents.IWorkflowRun) => void
  setMaxAllowableMonthlyPayment: (evaluations: CriteriaEvaluationResult[]) => void
  halfCostsMinusRoof: () => [number, number]
  unsetSensitiveData: () => void
  renderableData: (t?: ITenant, org?: Org) => Record<string, string>
  hasMinPrequalInfo: () => boolean
  hasMinCoborrowerPrequalInfo: () => boolean
  validateConsumerApplication_incomplete: (options?: IncompleteValidationOptions) => string[]
  requiresSpouse: () => boolean
  requiresSpouseSignature: () => boolean
  spouseName: () => string
  primaryBorrowerName: () => string
  coborrowerName: () => string
}

export type LoanApplication = BaseLoanApplication & Document

// servier loan application
export type SLoanApplication = LoanApplication & {
  _id: typeof mongo.ObjectId
  creditReports: typeof mongo.ObjectId[]
  referralSubmittedBy: typeof mongo.ObjectId | string | Installer
  toMaskedObject(): Partial<SLoanApplication>
  updateConsumerAssignableFields(o: Partial<SLoanApplication>): void
  getPresubmitErrors(): string[]
  getAutopayInterestRateSpread(): number
  getReadyForDocsErrors(): string[]
  getLookupHash(): string
  getCollateralDescription(): string
  addEnvelope(envelope: EnvelopeRecord): void
  updateEnvelope(envelopeId: string, status: string): void
  setAllEnvelopesVoided(kind: EnvelopeKind): Promise<SLoanApplication>
  shouldAutoSendLoanDocs(): boolean
  hasContractBeenUploaded(): boolean
  hasApprovedAgreementForFundReleaseType(fundReleaseType: string): boolean
  getApprovedFundReleaseRequest(
    fundReleaseType: string
  ): FundReleaseRequest
}

// client loan application
export type CLoanApplication = LoanApplication & {
  consumerId: string
  _id: string
}

export type SLoanAppField = keyof SLoanApplication
export type CLoanAppField = keyof CLoanApplication
export type LoanAppField = keyof LoanApplication

const DOCSUBTYPE_REQUIRED_COUNT: Record<DocSubType, number> = {
  weekly: 4,
  biWeekly: 2,
  monthly: 1,
  individual: 1,
  joint: 1,
}

export const getDocSubTypeRequiredCount = (docSubType: DocSubType | undefined): number | null => {
  if (!docSubType) return null
  return DOCSUBTYPE_REQUIRED_COUNT[docSubType]
}

export const getIncomeDocErrorsMultiUpload = (
  lapp: LoanApplication,
  docsArray: IncomeDocument[],
  coDocsArray: IncomeDocument[]
): string[] => {
  const errors: string[] = []

  const checkDocSet = (docSet: IncomeDocument, borrowerLabel: string) => {
    if (docSet.docType === 'paystubs') {
      const required = getDocSubTypeRequiredCount(docSet.docSubType)
      if (required === null) {
        errors.push(`Please select pay frequency for ${borrowerLabel}.`)
        return
      }
      const count = docSet.documents?.length ?? 0
      if (count !== required) {
        const label = required === 1 ? 'paystub' : 'paystubs'
        errors.push(`Exactly ${required} ${label} required for ${borrowerLabel}.`)
      }
    } else {
      // All other doc types (taxreturn, W-2, etc.) require exactly 1 document
      if ((docSet.documents?.length ?? 0) !== 1) {
        errors.push(`Exactly one ${docSet.docType} document required for ${borrowerLabel}.`)
      }
    }
  }

  if (docsArray) docsArray.forEach(doc => checkDocSet(doc, 'primary borrower'))
  if (lapp.isJointApp && coDocsArray) coDocsArray.forEach(doc => checkDocSet(doc, 'co-borrower'))

  return errors
}

export const usesSeventyThirtyFunding = (
  fundingSchedule?: FundingSchedule,
): boolean => fundingSchedule === FundingSchedule.SeventyThirty

export const getFundingScheduleLabel = (
  fundingSchedule?: FundingSchedule,
): string => {
  if (!fundingSchedule) {
    return ''
  }
  return FundingScheduleLabels[fundingSchedule]
}

export const getFundReleaseDetails = (
  type: string,
  installer: string,
  fundingSchedule: FundingSchedule,
) => {
  const isSeventyThirtyPath = fundingSchedule === FundingSchedule.SeventyThirty
  const initialPercentage = fundingSchedule === FundingSchedule.FiftyFifty
    ? '50%'
    : isSeventyThirtyPath
      ? '70%'
      : '20%'
  const finalPercentage = fundingSchedule === FundingSchedule.FiftyFifty
    ? '50%'
    : isSeventyThirtyPath
      ? '30%'
      : '80%'

  switch (type?.toLowerCase()) {
    case 'roof funding release':
      return {
        title: 'Roof Funding Release',
        description: `Your installer, ${installer}, has issued a Roof Funding Release Request as part of your solar installation project. This step is required to permit the bank to release these funds (representing 100% of the roof project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: '100% Completion of Roof Work:',
            detail: 'The roof must be fully repaired or replaced as per the project specifications.'
          },
          {
            title: 'Passed Inspection:',
            detail: 'The roof must pass all required inspections by the Authority Having Jurisdiction (AHJ). If the AHJ does not conduct inspections, the installer must provide a Completion Certificate, signed by the contractor, confirming the roof work is complete and meets all relevant standards.'
          }
        ]
      }
    case 'initial funding release':
      return {
        title: 'Initial Funding Release',
        description: `Your installer, ${installer}, has issued an Initial Funding Release Request 
          as part of your solar installation project. This step is required to permit the bank to release these funds 
          (representing the initial ${initialPercentage} of the solar project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: 'Solar Permit:',
            detail: 'The installer must obtain the required permit in compliance with state regulations. If a permit is not required in your area, confirm that you have an understanding of the project details and timeline.'
          },
          ...(isSeventyThirtyPath ? [{
            title: 'Lender Inspection Acknowledgment:',
            detail: 'You understand that release of the initial 70% may be contingent on a Lender Inspection to verify information provided in connection with the funding request. This is not a building code, safety, quality, or workmanship inspection.'
          }] : [])
        ]
      }
    case 'final funding release':
      return {
        title: 'Final Funding Release',
        description: `Your installer, ${installer}, has issued a Final Funding Release Request as part 
          of your solar installation project. This step is required to permit the bank to release these funds (representing 
          the final ${finalPercentage} of the solar project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: 'Solar Panel Installation:',
            detail: 'All solar panels are installed as expected.'
          },
          {
            title: 'Passed Inspection:',
            detail: 'The project must have passed all relevant inspections by the Authority Having Jurisdiction (AHJ).'
          },
          {
            title: 'Completion of Solar-Related Work:',
            detail: 'All solar-related work, including electrical connections and system set-up, must be completed as per the project specifications.'
          }
        ]
      }
    case 'full funding release':
      return {
        title: 'Full Funding Release',
        description: `Your installer, ${installer}, has issued a Full Funding Release Request as part of your solar installation project. This step is required to permit the bank to release these funds (representing 100% of the total project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: 'Completed Permitting:',
            detail: 'All relevant permits have been obtained in compliance with all applicable state and local regulations.'
          },
          {
            title: 'Solar Panel Installation:',
            detail: 'All solar panels are installed as expected.'
          },
          {
            title: 'Passed Inspection:',
            detail: 'The project must have passed all relevant inspections by the Authority Having Jurisdiction (AHJ).'
          },
          {
            title: 'Completion of Solar-Related Work:',
            detail: 'All solar-related work, including electrical connections and system set-up, must be completed as per the project specifications.'
          },
          {
            title: 'Completion of Non-Solar Work:',
            detail: 'All non-solar work agreed upon in the solar contract must also be completed.'
          }
        ]
      }
    default: null
  }
}

export const getCurrentFundRelease = (
  loanApplication: StatusData,
  fundReleaseType: string
) => {
  if (!loanApplication.fundReleasesRequested?.length || !fundReleaseType) return null
  return loanApplication.fundReleasesRequested.find(release => release.type === fundReleaseType)
}

// This helper function ensures that a fund release request is counted 
// as a required document when calculating the total number of documents 
// needed for an step.

export const getRequireDocsForStep = (
  lapp: StatusData,
  fundReleaseTypeForStep: string,
) => {
  const currentFundRelease = getCurrentFundRelease(lapp, fundReleaseTypeForStep)
  const fundReleaseRequiredCount = fundReleaseTypeForStep ? 1 : 0
  const fundReleaseCompletedCount = currentFundRelease?.agreementSigned.agrees ? 1 : 0
  let docsNeedActionCount = lapp.docsNeedInstallerActionCount
    + fundReleaseRequiredCount - fundReleaseCompletedCount

  // don't require docs until the application has been prequalified OR received
  if (!lapp.applicationReceived && lapp.isPrequalified === undefined) {
    docsNeedActionCount = 0
  }

  const requiredDocumentsCount = lapp.requiredDocuments
    ? Object.keys(lapp.requiredDocuments).length
    : 0

  return {
    fundReleaseRequiredCount,
    fundReleaseCompletedCount,
    requiredDocsCount: requiredDocumentsCount + fundReleaseRequiredCount,
    docsNeedActionCount
  }
}

export const getPayableAmounts = (
  fundingSchedule: FundingSchedule | undefined,
  roofFundingAmount: number,
  initialFundingAmount: number,
  finalFundingAmount: number,
  fullFundingAmount: number
): {
  payableAmount1: number,
  payableAmount2?: number,
  payableAmount3?: number
} => {

  let payableAmount1
  let payableAmount2
  let payableAmount3

  const schedule = fundingSchedule || FundingSchedule.FiftyFifty

  if (roofFundingAmount > 0) {
    switch (schedule) {
      case FundingSchedule.OneHundred:
        payableAmount1 = fullFundingAmount
        break
      case FundingSchedule.FiftyFifty:
      case FundingSchedule.TwentyEighty:
      case FundingSchedule.SeventyThirty:
        payableAmount1 = roofFundingAmount
        payableAmount2 = initialFundingAmount
        payableAmount3 = finalFundingAmount
        break
    }
  } else {
    switch (schedule) {
      case FundingSchedule.FiftyFifty:
      case FundingSchedule.TwentyEighty:
      case FundingSchedule.SeventyThirty:
        payableAmount1 = initialFundingAmount
        payableAmount2 = finalFundingAmount
        break
      case FundingSchedule.OneHundred:
        payableAmount1 = fullFundingAmount
        break
    }
  }

  return {
    payableAmount1,
    payableAmount2,
    payableAmount3
  }
}

export const getPaymentScheduleFields = (
  lapp: LoanApplication,
  amortization: AmortizationSchedule,
  mcgbIncentive: MCGBAmortizationResult,
  incentivePaymentMonths: number,
  interestOnlyPeriod: number,
  firstStandardPaymentDate: Date
) => {

  const { incentiveSelected, loanDurationMonths, interest } = lapp
  const mcgb = incentiveSelected && mcgbIncentive

  const numberOfPayments1 = incentiveSelected ? incentivePaymentMonths : loanDurationMonths - interestOnlyPeriod - 1
  const numberOfPayments2 = incentiveSelected ? loanDurationMonths - incentivePaymentMonths - interestOnlyPeriod - 1 : 1
  const numberOfPayments3 = incentiveSelected ? 1 : ''

  const whenPaymentsDue1 = monthDayYear(amortization.firstPaymentDate)
  const whenPaymentsDue2 = incentiveSelected ? `Monthly, beginning ${monthDayYear(firstStandardPaymentDate)}` : monthDayYear(amortization.finalPaymentDate)
  const whenPaymentsDue3 = incentiveSelected ? monthDayYear(amortization.finalPaymentDate) : ''

  const amountOfPayments1 = mcgb ? mcgbIncentive.incentiveMonthlyPayment : amortization.payment
  const amountOfPayments2 = mcgb ? `${formatted(mcgbIncentive.standardMonthlyPayment)}` : `${formatted(amortization.payment)}e`
  const amountOfPayments3 = mcgb ? `${formatted(mcgbIncentive.standardMonthlyPayment)}e` : ''

  const amountToDebit = mcgb ? formatted(mcgbIncentive.incentiveMonthlyPayment) : formatted(amortization.payment)
  const subsequentDebitAmount = mcgb ? `$${formatted(mcgbIncentive.standardMonthlyPayment)}` : ''
  const effectivePaymentDate = mcgb ? monthDayYear(firstStandardPaymentDate) : ''

  const stepRateDisclosure = incentiveSelected ? [
    'The annual interest rate applicable to this Loan will be a fixed rate that changes according to the schedule below.',
    `The starting interest rate will be ${MCGBLoanCalculations.INCENTIVE_RATE}% per annum.`,
    `Beginning on ${monthDayYear(firstStandardPaymentDate)}, the interest rate will increase to ${interest}% per annum and will remain until the Loan is paid in full.`
  ].join(' ')
    : ''

  return {
    numberOfPayments1,
    numberOfPayments2,
    numberOfPayments3,
    whenPaymentsDue1,
    whenPaymentsDue2,
    whenPaymentsDue3,
    amountToDebit,
    amountOfPayments1,
    amountOfPayments2,
    amountOfPayments3,
    stepRateDisclosure,
    subsequentDebitAmount,
    effectivePaymentDate
  }
}

export type OeAdminResubmitEligibilityFields = {
  error?: string | null
  preSubmitStatus?: string
  appId?: number | null
  installersStatus?: string
  isLocked?: boolean
  submittedFromIp?: string | null
  submittedTimestamp?: Date | string | null
}

export type OeAdminResubmitIneligibility =
  | { reason: 'locked' }
  | { reason: 'validation', message: string }

export function getOeAdminResubmitIneligibility(
  lapp: OeAdminResubmitEligibilityFields
): OeAdminResubmitIneligibility | null {
  if (!lapp.error) {
    return { reason: 'validation', message: 'application has no submission error' }
  }

  if (lapp.preSubmitStatus !== 'Submitted') {
    return { reason: 'validation', message: 'application pre-submit status must be Submitted' }
  }

  if (lapp.appId) {
    return { reason: 'validation', message: 'application already has an APP ID, cannot be resubmitted' }
  }

  if (lapp.installersStatus !== 'Awaiting Application') {
    return { reason: 'validation', message: 'application status must be Awaiting Application' }
  }

  if (!lapp.submittedFromIp) {
    return { reason: 'validation', message: 'application is missing original submission IP metadata' }
  }

  if (!lapp.submittedTimestamp) {
    return { reason: 'validation', message: 'application is missing original submission timestamp' }
  }

  if (lapp.isLocked) {
    return { reason: 'locked' }
  }

  return null
}

export function isEligibleForOeAdminResubmit(lapp: OeAdminResubmitEligibilityFields): boolean {
  return getOeAdminResubmitIneligibility(lapp) === null
}

// This helper function returns the example monthly payment increased for the autopay discount.
// The results are static because the example monthly payment is based on a fixed 
// interest rate of 6% and loan amount of $10,000.
export const getMonthlyPaymentExampleIncrease = (autopayDiscount: number) => {
  if (autopayDiscount === 1) {
    return 4.44
  }
  return 1.10
}

export const isCCEFAuthorized = (
  org: Org,
  installationAddress: StreetAddress,
) => {
  return (
    org.ccefAuthorized === 'Yes' &&
    installationAddress?.state === 'CO'
  )
}

export const shouldRedirectToCCEFPlatform = (
  org: Org,
  installationAddress: StreetAddress,
  {
    roofCost,
    equipmentType,
    loanAmount,
  }: {
    roofCost: number
    equipmentType?: EquipmentType | string
    loanAmount: number
  },
): boolean => {
  if (!isCCEFAuthorized(org, installationAddress)) {
    return false
  }

  const hasRoofOrTeslaRoof =
    roofCost !== 0 ||
    equipmentType === EquipmentType.TeslaRoof

  if (hasRoofOrTeslaRoof) {
    return false
  }

  if (loanAmount >= CCEF_PLATFORM_LOAN_AMOUNT_THRESHOLD) {
    return false
  }

  return true
}

export const isCcefLoanApplication = (
  lapp: { applicationReferralSource?: string | null },
): boolean => lapp.applicationReferralSource === CCEF_APPLICATION_REFERRAL_SOURCE